/**
 * LocationUpdateBanner
 *
 * - No location set: shows amber warning banner with "Set location now" link.
 * - Location set: shows a compact info card with coords and an "Edit" button.
 * Both open the same dialog (autocomplete + GPS).
 *
 * TO DISABLE: comment out <LocationUpdateBanner /> wherever it is rendered.
 */

import React, { useState, useCallback } from 'react';
import { IonIcon } from '@ionic/react';
import {
  closeOutline,
  createOutline,
  locationOutline,
  navigateOutline,
  warningOutline,
} from 'ionicons/icons';
import { AutoCompleteInput, OptionsType } from './AutoCompleteInput';
import {
  autocompleteAddress,
  lookupAddress,
  reverseGeocodeAddress,
  AddressLookupResult,
} from '../../services/logistics/logistics';
import { saveStore } from '../../services/stores/saveStore';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useQueryClient } from '@tanstack/react-query';
import useIonicToast from '../../hooks/useIonicToast';

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  /** The store's current geo_location value from Redux (null/undefined/"" = not set). */
  geoLocation: any;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseGeoLocation(
  geoLocation: any
): { lat: number; lng: number } | null {
  if (!geoLocation) return null;
  if (
    typeof geoLocation === 'object' &&
    'x' in geoLocation &&
    'y' in geoLocation
  ) {
    return { lng: Number(geoLocation.x), lat: Number(geoLocation.y) };
  }
  const match = String(geoLocation).match(
    /POINT\s*\(\s*([+-]?\d+\.?\d*)\s+([+-]?\d+\.?\d*)\s*\)/i
  );
  if (match) return { lng: parseFloat(match[1]), lat: parseFloat(match[2]) };
  return null;
}

// ─── Component ───────────────────────────────────────────────────────────────

const LocationUpdateBanner: React.FC<Props> = ({ geoLocation }) => {
  const [open, setOpen] = useState(false);
  const [pickedOption, setPickedOption] = useState<OptionsType | null>(null);
  const [selected, setSelected] = useState<AddressLookupResult | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const { presentIonToast } = useIonicToast();
  const queryClient = useQueryClient();

  const activeStore = useSelector(
    (state: RootState) => state.userStores.activeStore
  );
  const { signedAccessToken: authToken } = useSelector(
    (state: RootState) => state.userSession
  );

  const currentCoords = parseGeoLocation(geoLocation);
  const hasLocation = currentCoords !== null;

  // ─── Autocomplete fetch ─────────────────────────────────────────────────

  const fetchSuggestions = useCallback(
    async (query: string): Promise<OptionsType[]> => {
      if (query.trim().length < 2) return [];
      try {
        const suggestions = await autocompleteAddress(query);
        return (suggestions ?? []).map((s: any) => ({
          id: s.here_id,
          name: s.label,
        }));
      } catch {
        return [];
      }
    },
    []
  );

  // ─── Suggestion selected ────────────────────────────────────────────────

  const handleSelect = useCallback(
    async (option: OptionsType | null) => {
      setPickedOption(option);
      setSelected(null);
      if (!option) return;
      setLookingUp(true);
      try {
        const result = await lookupAddress(option.id);
        setSelected(result);
      } catch {
        presentIonToast('Could not fetch address details, please try again.');
        setPickedOption(null);
      } finally {
        setLookingUp(false);
      }
    },
    [presentIonToast]
  );

  // ─── GPS ────────────────────────────────────────────────────────────────

  const handleUseCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      presentIonToast('Geolocation is not supported by your browser.');
      return;
    }
    setGpsLoading(true);
    setPickedOption(null);
    setSelected(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const result = await reverseGeocodeAddress(
            pos.coords.latitude,
            pos.coords.longitude
          );
          setSelected(result);
          setPickedOption({ id: 'gps', name: result.label });
        } catch {
          presentIonToast('Could not determine address for your location.');
        } finally {
          setGpsLoading(false);
        }
      },
      () => {
        presentIonToast(
          'Location access denied. Please allow location and try again.'
        );
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [presentIonToast]);

  // ─── Close ──────────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    setOpen(false);
    setPickedOption(null);
    setSelected(null);
  }, []);

  // ─── Save ───────────────────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    if (!selected || !activeStore || !authToken) return;
    setSaving(true);
    try {
      await saveStore({
        authToken,
        body: {
          store_id: activeStore.id,
          geo_location: selected.coords,
          address: selected.address?.label ?? selected.label,
        } as any,
      });
      setOpen(false);
      presentIonToast('Store location saved.');
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    } catch {
      presentIonToast('Failed to save location, please try again.');
    } finally {
      setSaving(false);
    }
  }, [selected, activeStore, authToken, queryClient, presentIonToast]);

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <>
      {hasLocation ? (
        /* ── Location is set: info card with Edit button ── */
        <div className="flex items-start gap-3 rounded-lg border border-border bg-gray-50 dark:bg-gray-800/40 px-4 py-3 text-sm">
          <IonIcon
            icon={locationOutline}
            className="mt-0.5 shrink-0 text-primary text-base"
          />
          <div className="flex-1 min-w-0">
            <p className="font-semibold high-emphasis-text">Store location</p>
            <p className="medium-emphasis-text mt-0.5 font-mono text-xs">
              {currentCoords!.lat.toFixed(5)}, {currentCoords!.lng.toFixed(5)}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="shrink-0 flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            <IonIcon icon={createOutline} className="text-sm" />
            Edit
          </button>
        </div>
      ) : (
        /* ── No location: amber warning banner ── */
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 px-4 py-3 text-sm">
          <IonIcon
            icon={warningOutline}
            className="mt-0.5 shrink-0 text-amber-500 text-base"
          />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-amber-800 dark:text-amber-300">
              Store location not set
            </p>
            <p className="text-amber-700 dark:text-amber-400 mt-0.5">
              Add your store's location so delivery fees can be calculated
              accurately for your customers.
            </p>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="mt-2 inline-flex items-center gap-1.5 text-amber-800 dark:text-amber-300 font-semibold underline underline-offset-2 text-sm"
            >
              <IonIcon icon={locationOutline} className="text-base" />
              Set location now
            </button>
          </div>
        </div>
      )}

      {/* ── Dialog ── */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm px-4 pb-4 sm:pb-0">
          <div className="w-full max-w-md rounded-xl bg-white dark:bg-gray-900 border border-border shadow-xl p-5 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h5 className="font-bold text-base high-emphasis-text">
                  {hasLocation ? 'Update store location' : 'Set store location'}
                </h5>
                <p className="text-xs medium-emphasis-text mt-0.5">
                  Use GPS or search for your store address below.
                </p>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                aria-label="Close"
              >
                <IonIcon icon={closeOutline} className="text-xl" />
              </button>
            </div>

            {/* GPS button */}
            <button
              type="button"
              onClick={handleUseCurrentLocation}
              disabled={gpsLoading}
              className="w-full flex items-center justify-center gap-2 rounded-lg border border-dashed border-primary/50 bg-primary/5 hover:bg-primary/10 px-4 py-2.5 text-sm font-medium text-primary transition-colors disabled:opacity-50"
            >
              <IonIcon icon={navigateOutline} className="text-base" />
              {gpsLoading ? 'Detecting location…' : 'Use current location'}
            </button>

            <div className="relative flex items-center gap-2">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs medium-emphasis-text shrink-0">
                or search manually
              </span>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Autocomplete */}
            <AutoCompleteInput
              fetchOptions={fetchSuggestions}
              onChange={handleSelect}
              value={pickedOption}
              label="Store address"
              placeholder="e.g. 15 Adetokunbo Ademola Crescent, Wuse 2"
              size="xlg"
            />

            {lookingUp && (
              <p className="text-xs medium-emphasis-text">
                Fetching coordinates…
              </p>
            )}

            {selected && !lookingUp && (
              <div className="rounded-md bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 px-3 py-2 text-xs space-y-0.5">
                <p className="font-semibold text-green-800 dark:text-green-300">
                  Location confirmed
                </p>
                <p className="text-green-700 dark:text-green-400">
                  {selected.address.label}
                </p>
                <p className="text-green-600 dark:text-green-500 font-mono">
                  {+selected.coords.lat.toFixed(5)},{' '}
                  {+selected.coords.lng.toFixed(5)}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 rounded-lg border border-border px-4 py-2 text-sm medium-emphasis-text hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!selected || saving || lookingUp}
                className="flex-1 rounded-lg bg-primary text-white px-4 py-2 text-sm font-semibold disabled:opacity-50 transition-opacity"
              >
                {saving ? 'Saving…' : 'Save location'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LocationUpdateBanner;
